import {
  type CrossroadsLog,
  LOG_OVERVIEW_PAGE_SIZE,
  type LogGroup,
  type LogOverviewRow,
  type LoggingOverviewDateRange,
} from '@/api/hooks/useIntegrationLogs'
import { ControlBarSearch } from '@/components/Display/ControlBarSearch'
import { EnvModeSwitch } from '@/components/Display/EnvModeSwitch'
import { useTenantStore } from '@/stores/tenantStore'
import type { EnvModeSelection } from '@/utils/envMode'
import { ReloadOutlined } from '@ant-design/icons'
import {
  GraviButton,
  GraviGrid,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import type {
  FirstDataRenderedEvent,
  GridApi,
  IServerSideDatasource,
  SelectionChangedEvent,
} from 'ag-grid-community'
import { Switch } from 'antd'
import type { GraviGridProps } from 'node_modules/@gravitate-js/excalibrr/dist/components/GraviGrid/index.types'
import { useCallback, useMemo, useState } from 'react'
import { LogsRangePicker } from '../../LogsRangePicker'
import { BulkRetryButton } from './BulkRetryButton'
import { BulkSilenceButton } from './BulkSilenceButton'
import { LogDetailsSidebar } from './LogDetailsSidebar'
import { useLogColumnDefs } from './LogsColumnDefs'
import { LogsFilterPopover } from './LogsFilterPopover'
import type { LogFilters, OwnerOption } from './logsFilterModel'
import { useLogDetailGrid } from './useLogDetailGrid'
import { useLogSidebar } from './useLogSidebar'

interface LogsGridProps {
  serverSideDatasource: IServerSideDatasource
  rowCount: number | undefined
  isLoading: boolean
  getFilterOptions: (
    column: string,
    filterModel: Record<string, unknown>,
  ) => Promise<(string | null)[]>
  onRefresh: () => void
  gridApiRef: React.MutableRefObject<GridApi | null>
  showExceptionsOnly: boolean | null
  setShowExceptionsOnly: (show: boolean) => void
  hideSilenced: boolean | null
  setHideSilenced: (hide: boolean) => void
  onSearchChange: (value: string) => void
  /** Controlled, because a ?order= deep link fills the box too. */
  searchValue: string
  dateRange?: LoggingOverviewDateRange
  onDateRangeChange: (dateRange: LoggingOverviewDateRange | undefined) => void
  logFilters: LogFilters
  onLogFiltersChange: (filters: LogFilters) => void
  ownerOptions: OwnerOption[]
  mode: EnvModeSelection
  onModeChange: (mode: EnvModeSelection) => void
}

export function LogsGrid({
  serverSideDatasource,
  rowCount,
  isLoading,
  getFilterOptions,
  onRefresh,
  gridApiRef,
  showExceptionsOnly,
  setShowExceptionsOnly,
  hideSilenced,
  setHideSilenced,
  onSearchChange,
  searchValue,
  dateRange,
  onDateRangeChange,
  logFilters,
  onLogFiltersChange,
  ownerOptions,
  mode,
  onModeChange,
}: LogsGridProps) {
  const {
    selectedLog,
    selectedLogGroupId,
    selectedGroupMetadata,
    selectLog,
    onCellClicked,
    clearSelectedLog,
  } = useLogSidebar()
  const { selectedTenant } = useTenantStore()

  const { getRowId, detailCellRendererParams, auditModalElement } =
    useLogDetailGrid({ onLogSelected: selectLog })

  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent<LogOverviewRow>) => {
      gridApiRef.current = params.api
    },
    [gridApiRef],
  )

  const [selectedRows, setSelectedRows] = useState<LogOverviewRow[]>([])
  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<LogOverviewRow>) => {
      setSelectedRows(event.api.getSelectedRows())
    },
    [],
  )

  const clearSelection = useCallback(() => {
    gridApiRef.current?.deselectAll()
    setSelectedRows([])
  }, [gridApiRef])

  // Writes the saved checkpoints straight onto the rows, so the dots and the
  // dimming update while the menu is still open. A refetch is still needed for
  // Hide Silenced to drop the row, but that waits until the menu closes.
  const applySilencing = useCallback(
    (groups: LogGroup[]) => {
      const api = gridApiRef.current
      if (!api) return
      for (const group of groups) {
        if (!group._id) continue
        const node = api.getRowNode(String(group._id))
        if (node?.data)
          node.setData({ ...node.data, checkpoints: group.checkpoints })
      }
    },
    [gridApiRef],
  )

  const onBulkSilenced = useCallback(
    (groups: LogGroup[]) => {
      applySilencing(groups)
      clearSelection()
      onRefresh()
    },
    [applySilencing, clearSelection, onRefresh],
  )

  const onBulkRetryStarted = useCallback(() => {
    clearSelection()
    // The submitted rows are now mid-retry, so the page they came from is stale.
    onRefresh()
  }, [clearSelection, onRefresh])

  // Clicking a checkpoint in the dots' popover opens that saga's latest event
  // in the sidebar. The row is in hand here, so unlike the detail grid's
  // rows this can set the group metadata the panel heads its details with.
  const selectCheckpointLog = useCallback(
    (log: CrossroadsLog, row: LogOverviewRow) =>
      selectLog(log, row.id, row.metadata),
    [selectLog],
  )

  const columnDefs = useLogColumnDefs(
    selectedTenant?.name ?? '',
    selectCheckpointLog,
  )

  const agPropOverrides = useMemo(
    () =>
      ({
        columnDefs,
        rowModelType: 'serverSide',
        serverSideDatasource,
        pagination: true,
        paginationPageSize: LOG_OVERVIEW_PAGE_SIZE,
        cacheBlockSize: LOG_OVERVIEW_PAGE_SIZE,
        context: { getFilterOptions, refreshGrid: onRefresh, applySilencing },
        domLayout: 'normal',
        rowHeight: 45,
        animateRows: true,
        getRowId,
        rowGroupPanelShow: 'onlyWhenGrouping',
        sortingOrder: ['desc', 'asc'],
        rowSelection: 'multiple',
        masterDetail: true,
        suppressRowClickSelection: true,
        detailCellRendererParams,
        detailRowHeight: 500,
        isRowMaster: () => true,
        onCellClicked,
        onFirstDataRendered,
      }) satisfies GraviGridProps<
        LogOverviewRow,
        Record<string, unknown>
      >['agPropOverrides'],
    [
      columnDefs,
      serverSideDatasource,
      getFilterOptions,
      onRefresh,
      applySilencing,
      onCellClicked,
      detailCellRendererParams,
      getRowId,
      onFirstDataRendered,
    ],
  )

  return (
    <Horizontal gap={16} className='h-full' bg='elevated'>
      <Vertical flex={2.5}>
        <GraviGrid<LogOverviewRow, Record<string, unknown>>
          columnDefs={columnDefs}
          loading={isLoading}
          columnDefaultOverrides={{ resizable: true, enableRowGroup: false }}
          // GraviGrid composes this into its own selection handler; setting it
          // through agPropOverrides would replace that handler instead.
          onSelectionChanged={onSelectionChanged}
          // Shrinks the action-button region to its content so the search
          // region gets the slack -- it now carries the mode switch too.
          fullWidthSearchBar
          controlBarProps={{
            rowCount,
            title: 'Order Integration Logs',
            customSearchBar: (
              <ControlBarSearch onChange={onSearchChange} value={searchValue} />
            ),
            // 20 between controls, 6 inside a label-and-control pair, so a
            // switch reads as belonging to the label beside it.
            actionButtons: (
              <Horizontal gap={20} alignItems='center'>
                {selectedRows.length > 0 && (
                  <>
                    <BulkRetryButton
                      selectedRows={selectedRows}
                      onStarted={onBulkRetryStarted}
                    />
                    <BulkSilenceButton
                      selectedRows={selectedRows}
                      onDone={onBulkSilenced}
                    />
                  </>
                )}
                <LogsFilterPopover
                  value={logFilters}
                  onChange={onLogFiltersChange}
                  ownerOptions={ownerOptions}
                />
                <EnvModeSwitch value={mode} onChange={onModeChange} />
                <Horizontal gap={6} alignItems='center'>
                  <Texto>Hide Silenced</Texto>
                  <Switch
                    checked={Boolean(hideSilenced)}
                    onChange={setHideSilenced}
                  />
                </Horizontal>
                <Horizontal gap={6} alignItems='center'>
                  <Texto>Show Issues Only</Texto>
                  <Switch
                    checked={Boolean(showExceptionsOnly)}
                    onChange={setShowExceptionsOnly}
                  />
                </Horizontal>
                <LogsRangePicker
                  value={dateRange}
                  onChange={onDateRangeChange}
                />
                <GraviButton
                  size='small'
                  appearance='outline'
                  color='blue'
                  icon={<ReloadOutlined />}
                  onClick={onRefresh}
                >
                  Refresh
                </GraviButton>
              </Horizontal>
            ),
          }}
          agPropOverrides={agPropOverrides}
        />
      </Vertical>
      <LogDetailsSidebar
        selectedLog={selectedLog}
        groupMetadata={selectedGroupMetadata}
        selectedTenantName={selectedTenant?.name}
        logGroupId={selectedLogGroupId}
        refetchLogs={onRefresh}
        onClose={clearSelectedLog}
      />
      {auditModalElement}
    </Horizontal>
  )
}
